/* eslint-disable no-unused-vars */
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  InputAdornment,
  makeStyles,
  TextField,
  Typography
} from '@material-ui/core';
import { Page } from 'src/components';
import {
  EcomOrdersListToolbar as Toolbar,
  PurchaseOrderListTable
} from './components';
import { LoaderBar } from 'src/components';
import { debounce } from 'lodash';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { Pagination } from '@material-ui/lab';
import { FieldArray, Formik, FormikProps, useFormik } from 'formik';
import { IconButton } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';

import useUserInfo from 'src/hooks/user/use-user-info';
import { formatCurrency } from 'src/utils';
import { colors } from 'src/constants';
import {
  CreatePurchaseOrderPayload,
  PurchaseOrderItem
} from 'src/redux/slices/purchase-order/types';
import { PurchaseOrderForm } from './components/PurchaseOrderFormV2';
import { unwrapResult } from '@reduxjs/toolkit';
import { usePermissions, useRoles, useSnackBar } from 'src/hooks';
import { initialPurchaseOrderValues } from 'src/redux/slices/purchase-order';
import { useNavigate } from 'react-router';
import usePrompt from 'src/utils/navigation-prompt';
import SystemUpdateAltIcon from '@material-ui/icons/SystemUpdateAlt';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import { getPurchaseOrderResponseFormikTransformer } from 'src/redux/slices/purchase-order/utils';
import { getVat2307Value } from 'src/utils/vatUtils';

const {
  actions: ecomOrderActions,
  selectors: ecomOrderSelectors
} = slices.ecomOrder;

const { actions: purchaseOrderActions } = slices.purchaseOrder;

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

export const component = () => {
  const classes = useStyles();
  const snackbar = useSnackBar();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { getRoles } = useRoles();

  const { canCreateRMA } = usePermissions();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // TODO: Use this later once an endpoint is available to be able to get PO Details via PO No.
  const [duplicateInputVisible, setDuplicateInputVisible] = useState<boolean>(
    false
  );

  const onPressCreate = async (values: CreatePurchaseOrderPayload) => {
    setIsLoading(true);
    const productsWithSIPercentage = values?.products;

    const { si_percentage, ...rest } = values;
    const payload = {
      ...rest,
      ...(si_percentage?.toString().trim() !== '' && {
        si_percentage
      }),
      terms: values.terms ? `${parseInt(values.terms.toString())} days` : '',
      products: productsWithSIPercentage
    };

    const response = unwrapResult(
      await dispatch(purchaseOrderActions.createPurchaseOrderThunk(payload))
    );

    if (response?.success && response?.originalData?.data) {
      const newPoId = response?.originalData?.data?.po_id;
      snackbar.show({
        message: response?.message || 'Purchase Order Created',
        severity: 'success'
      });
      formikRef?.resetForm();
      requestAnimationFrame(() => {
        navigate(`/app/purchase-order/${newPoId}`);
      });
    } else {
      snackbar.show({
        message: `${response?.message || 'Failed to create Purchase Order'}, ${
          response?.errors ? JSON.stringify(response?.errors) : ''
        }`,
        severity: 'error'
      });
    }
    setIsLoading(false);
  };

  const handleSubmitWithValidation = async () => {
    const errors = await formikRef.validateForm();
    const errorsArr = Object.entries(errors) ?? [];
    if (errorsArr.length > 0) {
      snackbar.show({
        message: `${errorsArr[0][1]}`,
        severity: 'error'
      });
    } else {
      await formikRef.handleSubmit();
    }
  };

  // Later support PO No. duplication
  // This function can be used to fetch PO values based on a duplicate PO number
  // For now, we will just log the input value
  const onPODuplicate = async (poId: string) => {
    setIsLoading(true);

    const response = unwrapResult(
      await dispatch(
        purchaseOrderActions.getPurchaseOrderDetailsThunk(Number(poId))
      )
    );

    if (response?.success && response?.originalData?.data) {
      const responseData = getPurchaseOrderResponseFormikTransformer(
        response.originalData.data
      );
      formikRef.setValues(responseData, false);
      const urlPath = `/app/purchase-order/add`;
      // To update the URL without reloading the page.
      // window?.history?.pushState({}, '', urlPath);
    } else {
      snackbar.show({
        message: `${response?.message || 'Failed to fetch Purchase Order'}, ${
          response?.errors ? JSON.stringify(response?.errors) : ''
        }`,
        severity: 'error'
      });
    }
    setIsLoading(false);
  };

  const formSchema = Yup.object().shape({
    branch_id: Yup.number().required('Branch is required'),
    supplier_id: Yup.number().required('Supplier is required'),
    products: Yup.array().min(1, 'At least one products is required')
  });

  const formikRef = useFormik({
    initialValues: initialPurchaseOrderValues,
    enableReinitialize: true,
    onSubmit: onPressCreate,
    validationSchema: formSchema
  });

  usePrompt(
    'There are some unsaved changes. Are you sure you want to leave?',
    formikRef.dirty
  );

  // const isPoSupervisor = getRoles('purchasing supervisor');

  useEffect(() => {
    const search = location?.search;
    const params = new URLSearchParams(search);
    const dupPoId = params?.get('dup_po_id');

    if (dupPoId) {
      onPODuplicate(dupPoId);
      // Here you can fetch the PO details based on the dup_po_id
      // getPOValuesViaPONo(dupPoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Page className={classes.root} title="Create Purchase Orders">
      <Container maxWidth={false}>
        <Box
          display="flex"
          flexDirection="column"
          // justifyContent="space-between"
          mb={1}
        >
          <Typography color="textPrimary" gutterBottom variant="h3">
            New Purchase Order
          </Typography>
          {duplicateInputVisible ? (
            <TextField
              fullWidth
              label="Duplicate PO No"
              size="small"
              variant="outlined"
              name="dup_po_no"
              style={{ marginTop: 10, marginBottom: 10 }}
              onKeyDown={(e: any) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setDuplicateInputVisible(false);
                  console.log('Duplicate PO No:', e.target.value);
                  // Here you can handle the duplicate PO logic
                }
              }}
            />
          ) : null}
        </Box>

        <PurchaseOrderForm formikRef={formikRef} isEditable={true} />

        <Box
          mb={2}
          mt={4}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Button
            disabled={isLoading}
            style={{ paddingTop: 16, paddingBottom: 16 }}
            fullWidth
            color="primary"
            size="large"
            type="submit"
            variant="contained"
            onClick={handleSubmitWithValidation}
          >
            <SystemUpdateAltIcon style={{ marginRight: 8 }} />
            Create Purchase Order
          </Button>

          {/* {isPoSupervisor ? (
            <Box display="flex">
              <Button
                variant="contained"
                onClick={() => {}}
                style={{
                  marginRight: 8,
                  paddingTop: 16,
                  paddingBottom: 16,
                  backgroundColor: colors.lightGreen[500],
                  color: '#fff'
                }}
              >
                <CheckCircleOutlineIcon style={{ marginRight: 8 }} />
                Approved
              </Button>
              <Button color="secondary" variant="outlined" onClick={() => {}}>
                <CloseIcon style={{ marginRight: 8 }} />
                Declined
              </Button>
            </Box>
          ) : null} */}
        </Box>
      </Container>
    </Page>
  );
};

export const CreatePurchaseOrder = React.memo(component);
